import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AppHeader from "@/components/AppHeader";
import Tip from "@/components/Tip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PenLine, Upload, Trash2, Save, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

function SignaturePad({ value, onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(!value);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#004a99"; ctx.lineWidth = 2.5; ctx.lineCap = "round";
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height);
      img.src = value;
      setEmpty(false);
    }
  }, [value]);

  const pos = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const evt = e.touches ? e.touches[0] : e;
    return { x: (evt.clientX - rect.left) * (ref.current.width / rect.width),
             y: (evt.clientY - rect.top) * (ref.current.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setEmpty(false); };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(ref.current.toDataURL("image/png")); } };
  const clear = () => { const c = ref.current; const ctx = c.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height); setEmpty(true); onChange(null); };

  return (
    <div>
      <canvas ref={ref} width={520} height={180} className="border-2 border-dashed border-[#004a99]/40 rounded-xl w-full max-w-[520px] cursor-crosshair bg-white touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas" />
      <div className="flex gap-2 mt-2">
        <Button type="button" variant="outline" onClick={clear} data-testid="signature-clear" className="text-slate-600">
          <Trash2 className="h-4 w-4 mr-1" /> Clear
        </Button>
        <span className="text-xs text-slate-500 self-center">{empty ? "Draw your signature above" : "✓ Captured"}</span>
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [drawn, setDrawn] = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", registration_no: "", qualifications: "" });

  useEffect(() => {
    api.get("/auth/profile").then((r) => {
      setProfile(r.data);
      setForm({
        name: r.data.name || "", phone: r.data.phone || "",
        registration_no: r.data.registration_no || "",
        qualifications: r.data.qualifications || "",
      });
      setDrawn(r.data.signature_base64 || null);
      setUploaded(r.data.signature_url || null);
    });
  }, []);

  const onUploadFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Max 2MB image"); return; }
    const reader = new FileReader();
    reader.onload = () => setUploaded(reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!drawn && !uploaded) { toast.error("Please draw OR upload your signature"); return; }
    setSaving(true);
    try {
      const r = await api.patch("/auth/profile", {
        ...form,
        signature_base64: drawn || null,
        signature_url: uploaded || null,
      });
      toast.success("Profile saved · prescriptions are now unlocked");
      if (setUser) setUser({ ...user, ...r.data.user });
      // Auto redirect back if came from a blocked prescription attempt
      const back = localStorage.getItem("pulse_post_profile_redirect");
      if (back) { localStorage.removeItem("pulse_post_profile_redirect"); navigate(back); }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Save failed");
    } finally { setSaving(false); }
  };

  if (!profile) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen app-bg pb-16">
      <AppHeader title="My Profile" subtitle="Doctor identity, signature & credentials" badge={profile.role} />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 mt-6 sm:mt-8 space-y-5 sm:space-y-6">
        <section className="solid-panel p-6 shadow-3d">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-xl bg-[#e6f0ff] flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-[#004a99]" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-bold">Doctor signature <Tip text="Without your signature, prescriptions cannot be generated or shared. This protects your patients legally." /></h2>
              <p className="text-sm text-slate-500">Draw it OR upload an image. Used on every prescription PDF.</p>
            </div>
          </div>

          <Tabs defaultValue="draw">
            <TabsList className="bg-white border border-slate-200 p-1 rounded-xl">
              <TabsTrigger value="draw" data-testid="sig-tab-draw"><PenLine className="h-4 w-4 mr-1.5" /> Draw</TabsTrigger>
              <TabsTrigger value="upload" data-testid="sig-tab-upload"><Upload className="h-4 w-4 mr-1.5" /> Upload</TabsTrigger>
            </TabsList>
            <TabsContent value="draw" className="mt-4">
              <SignaturePad value={drawn} onChange={setDrawn} />
            </TabsContent>
            <TabsContent value="upload" className="mt-4">
              <input type="file" accept="image/*" onChange={onUploadFile} data-testid="signature-upload-input" className="block text-sm" />
              {uploaded && (
                <div className="mt-3 p-3 bg-white border-2 border-dashed border-[#004a99]/40 rounded-xl inline-block">
                  <img src={uploaded} alt="signature" className="h-24 object-contain" />
                  <button onClick={() => setUploaded(null)} className="ml-3 text-sm text-red-600 underline" data-testid="signature-upload-remove">Remove</button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </section>

        <section className="solid-panel p-6 shadow-3d">
          <h2 className="text-xl font-heading font-bold mb-4">Credentials <Tip text="These appear on every prescription PDF below your signature." /></h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" /></div>
            <div><Label>Registration No.</Label><Input value={form.registration_no} onChange={(e) => setForm({ ...form, registration_no: e.target.value })} placeholder="DMC / MCI / state council" data-testid="profile-regno" /></div>
            <div><Label>Qualifications</Label><Input value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} placeholder="MBBS, MD (Internal Medicine)" data-testid="profile-quals" /></div>
          </div>
        </section>

        <Button onClick={save} disabled={saving} className="bg-[#f37021] hover:bg-[#d9621b] text-white px-6 py-6 text-base" data-testid="profile-save">
          <Save className="h-5 w-5 mr-2" /> {saving ? "Saving…" : "Save Profile"}
        </Button>
      </main>
    </div>
  );
}
